import React from "react";
import { Link } from "gatsby";
import dayjs from "dayjs";
import Img from "gatsby-image";
import { breakPoints } from "~/utils/variables";
import { colors } from "~/utils/colors";
import { PATH } from "~/constant/path";

type Props = {
  articles: GatsbyTypes.ContentfulInsightArticleConnection;
  categories: GatsbyTypes.ContentfulInsightCategoryConnection;
  currentCategoryId?: string;
  isSearchMode?: boolean;
  searchQuery?: string;
};

export const InsightArticles: React.FC<Props> = ({
  articles,
  categories,
  currentCategoryId,
  isSearchMode = false,
  searchQuery = "",
}) => {
  return (
    <>
      <style jsx>{`
        .wrapper {
          display: flex;
          flex-direction: column;
          width: 100%;
        }
        .contentTitle {
          font-weight: 700;
        }
        .contentTab {
          display: flex;
          list-style: none;
        }
        .contentTabItem {
          color: ${colors.lightText};
          position: relative;
        }
        .contentTabItemActive {
          font-weight: bold;
          color: ${colors.defaultText};
          position: relative;
        }
        .contentTabItem :global(a) {
          color: ${colors.lightText};
        }
        .contentTabItem :global(a:hover) {
          color: ${colors.orange};
        }
        .contentTabItemActive :global(a) {
          color: ${colors.defaultText};
        }
        .contentTabItemActive :global(a:hover) {
          color: ${colors.defaultText};
        }
        .contentTabItemActive:after {
          content: "";
          background-color: ${colors.orange};
          width: 100%;
          position: absolute;
          bottom: 0;
          height: 3px;
          border-radius: 6px;
          left: 0;
          bottom: -10px;
        }
        .articles {
          margin: 30px auto 0;
          width: 100%;
        }
        .articleImage {
          display: block;
          object-fit: cover;
          box-shadow: none;
          border-radius: 8px;
          overflow: hidden;
          width: 100%;
          transition: box-shadow 0.3s ease;
        }
        .articleImage :global(.gatsby-image-wrapper),
        .articleImage :global(img) {
          border-radius: 8px;
        }
        .article:hover .articleImage {
          box-shadow: 0 4px 32px rgba(0, 0, 0, 0.1);
        }
        .articleHeader {
          display: flex;
          margin-top: 15px;
          width: 100%;
          align-items: center;
        }
        .articleHeader :global(.label) {
          border-radius: 30px;
          font-weight: bold;
          font-size: 1.2rem;
          padding: 4px 12px;
          background: transparent;
          color: #ff7400;
          border: 1px solid #ff7400;
          text-decoration: none;
          display: inline-block;
        }
        .articleHeader :global(.label:hover) {
          opacity: 0.8;
        }
        .publishedAt {
          text-align: right;
          color: ${colors.lightText};
          font-size: 1.2rem;
          margin-left: auto;
        }
        .articleTitle {
          color: ${colors.defaultText};
          font-size: 1.7rem;
          font-weight: 500;
          margin: 10px 0 0;
          transition: color 0.3s ease;
        }
        .article:hover .articleTitle {
          color: #ff6400;
        }
        .noResults {
          margin: 30px 0;
          text-align: center;
          color: ${colors.lightText};
          font-size: 1.6rem;
        }
        @media (${breakPoints.sp}) {
          .contentTitle {
            font-size: 2.1rem;
          }
          .contentTab {
            margin-top: 20px;
            flex-wrap: nowrap;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            padding-bottom: 12px;
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .contentTab::-webkit-scrollbar {
            display: none;
          }
          .contentTabItem {
            font-size: 1.3rem;
            padding: 0 5px;
            flex-shrink: 0;
            white-space: nowrap;
          }
          .contentTabItemActive {
            font-size: 1.3rem;
            padding: 0 5px;
            flex-shrink: 0;
            white-space: nowrap;
          }
          .articles {
            margin: 0 auto;
            width: 100%;
          }
          .article {
            margin-top: 50px;
            width: 100%;
          }
          .article:first-child {
            margin-top: 30px;
          }
        }
        @media (${breakPoints.minPc}) {
          .contentHeader {
            display: flex;
            align-items: flex-start;
          }

          .contentTitle {
            font-size: 2.4rem;
          }
          .contentTab {
            margin-left: 50px;
            margin-top: 8px;
          }
          .contentTabItem {
            font-size: 1.5rem;
            padding: 0 10px;
          }
          .contentTabItemActive {
            font-size: 1.5rem;
            padding: 0 10px;
          }
          .articles {
            display: flex;
            flex-wrap: wrap;
          }
          .article {
            width: calc(50% - 30px);
          }
          .article:nth-child(odd) {
            margin: 0 0 30px;
          }
          .article:nth-child(even) {
            margin: 0 0 30px 30px;
          }
        }
      `}</style>
      <div className="wrapper">
        {!isSearchMode && (
          <div className="contentHeader">
            <h2 className="contentTitle">新着記事</h2>
            <ul className="contentTab">
              <li className={(!currentCategoryId && `contentTabItemActive`) || `contentTabItem`}>
                <Link to={PATH.INSIGHT.to}>すべて</Link>
              </li>
              {categories.edges.map((edge) => (
                <li
                  key={`categories_${edge.node.contentful_id}`}
                  className={
                    (currentCategoryId && currentCategoryId === edge.node.contentful_id && `contentTabItemActive`) ||
                    `contentTabItem`
                  }
                >
                  <Link to={`${PATH.INSIGHT.to}/${edge.node.slug}`}>{edge.node.name}</Link>
                </li>
              ))}
            </ul>
          </div>
        )}
        {isSearchMode && (
          <div className="contentHeader">
            <h2 className="contentTitle">「{searchQuery}」の検索結果</h2>
          </div>
        )}
        <div className="articles">
          {articles.edges.length === 0 && isSearchMode ? (
            <div className="noResults">検索結果が見つかりませんでした</div>
          ) : (
            articles.edges.map((edge) => {
              const { category, contentful_id, path, publishedAt, title } = edge.node;
              return (
                <article key={`articles_${contentful_id}`} className="article">
                  <Link to={`${path || `${PATH.INSIGHT.to}/${contentful_id}`}`}>
                    <div className="articleImage">
                      <Img fluid={edge.node.cover?.fluid} />
                    </div>
                    <div className="articleHeader">
                      {category?.map((c) => (
                        <Link
                          key={`category_${contentful_id}_${c?.contentful_id}`}
                          to={`${PATH.INSIGHT.to}/${c?.slug}`}
                          className="label"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {c?.name}
                        </Link>
                      ))}
                      <div className="publishedAt">{dayjs(publishedAt).format("YYYY.MM.DD")}</div>
                    </div>
                    <h2 className="articleTitle">{title}</h2>
                  </Link>
                </article>
              );
            })
          )}
        </div>
      </div>
    </>
  );
};
